import { useMutation, useQuery } from "@tanstack/react-query";

import { organizationOauthClientApi } from "src/api/taktileApi";
import {
  ServiceAccountOAuthClientInfo,
  ServiceAccountOAuthClientKeyPost,
  ServiceAccountOAuthClientPatch,
  ServiceAccountOAuthClientPatchStatusEnum,
  ServiceAccountOAuthClientPost,
} from "src/clients/taktile-api";
import { queryClient } from "src/queryClient";

const oauthClientKeys = {
  all: ["workspaceOauthClients"] as const,
  list: (orgId: string, wsId: string) =>
    [...oauthClientKeys.all, orgId, wsId] as const,
};

export const useWorkspaceOAuthClients = (orgId: string, wsId: string) =>
  useQuery<ServiceAccountOAuthClientInfo[], Error>({
    queryKey: oauthClientKeys.list(orgId, wsId),
    queryFn: async () =>
      (
        await organizationOauthClientApi.getAllOauthClientsApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsGet(
          orgId,
          wsId,
        )
      ).data,
  });

export const useCreateOAuthClient = (orgId: string, wsId: string) =>
  useMutation({
    mutationFn: (payload: ServiceAccountOAuthClientPost) =>
      organizationOauthClientApi.createOauthClientApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsPost(
        orgId,
        wsId,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthClientKeys.all });
    },
  });

export const useUpdateOAuthClient = (
  orgId: string,
  wsId: string,
  clientId: string,
) =>
  useMutation({
    mutationFn: (payload: ServiceAccountOAuthClientPatch) =>
      organizationOauthClientApi.patchOauthClientApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsClientIdPatch(
        orgId,
        wsId,
        clientId,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthClientKeys.all });
    },
  });

export const useSetOAuthClientStatus = (orgId: string, wsId: string) =>
  useMutation({
    mutationFn: ({
      clientId,
      status,
    }: {
      clientId: string;
      status: ServiceAccountOAuthClientPatchStatusEnum;
    }) =>
      organizationOauthClientApi.patchOauthClientApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsClientIdPatch(
        orgId,
        wsId,
        clientId,
        { status },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthClientKeys.all });
    },
  });

export const useDeleteOAuthClient = (
  orgId: string,
  wsId: string,
  clientId: string,
) =>
  useMutation({
    mutationFn: () =>
      organizationOauthClientApi.deleteOauthClientApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsClientIdDelete(
        orgId,
        wsId,
        clientId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthClientKeys.all });
    },
  });

export const useCreateOAuthClientKey = (
  orgId: string,
  wsId: string,
  clientId: string,
) =>
  useMutation({
    mutationFn: (payload: ServiceAccountOAuthClientKeyPost) =>
      organizationOauthClientApi.createOauthClientKeyApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsClientIdKeysPost(
        orgId,
        wsId,
        clientId,
        payload,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthClientKeys.all });
    },
  });

export const useDeleteOAuthClientKey = (
  orgId: string,
  wsId: string,
  clientId: string,
) =>
  useMutation({
    mutationFn: (kid: string) =>
      organizationOauthClientApi.deleteOauthClientKeyApiV1OrganizationsOrganizationIdWorkspacesWorkspaceIdOauthClientsClientIdKeysKidDelete(
        orgId,
        wsId,
        clientId,
        kid,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: oauthClientKeys.all });
    },
  });
